import React, { useRef, useState } from 'react';
import { CopyIconBlue } from "assests/GmsIcons";
import { toast } from 'Components/DesignSystem/Toast/useToast';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import { PromotedModelOption } from '../Types';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';

type AppletCardProps = {
    applet: AppletResponse;
    setAppletData: React.Dispatch<React.SetStateAction<AppletResponse[] | null>>;
    promotedModels: PromotedModelOption[];
    onModelChange: (appletId: string, modelId: string | null, modelName: string) => void;
};

const AppletCard = ({
    applet,
    setAppletData,
    promotedModels,
    onModelChange
}: AppletCardProps) => {
    const [note, setNote] = useState<string>(applet.placement_note);
    const noteInputRef = useRef<HTMLTextAreaElement>(null);

    const handleClipboard = () => {
        navigator.clipboard.writeText(applet?.applet).then(() => toast.success("Copied applet key"));
    };

    const handleModelSelect = (value: string | number) => {
        const selectedModel = promotedModels.find(m => m.model_id === value);

        onModelChange(
            applet.applet_id,
            value === "" ? null : value.toString(),
            selectedModel?.model_name || ""
        );
    };

    const handlePlacementNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setNote(value);

        // Update the applet data in parent state
        setAppletData(prev => {
            if (!prev) return prev;

            return prev.map(item =>
                item.applet_id === applet?.applet_id
                    ? { ...item, placement_note: value }
                    : item
            );
        });
    };

    // Transform promoted models to dropdown options
    const modelOptions = promotedModels.map(model => ({
        label: model.model_name,
        value: model.model_id
    }));

    return (
        <div className="tw-rounded-md tw-border tw-border-zinc-300 tw-bg-white tw-p-4">
            <div className="tw-flex tw-items-center tw-mb-4">
                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-pr-1">
                    {applet.applet_name}
                </h3>

                {applet?.ab_testing_enabled && (
                    <div className="tw-self-stretch tw-px-2 tw-py-0.5 tw-bg-[#FCF6E3] tw-rounded-[3px] tw-inline-flex tw-justify-center tw-items-center tw-gap-1">
                        <div className="tw-justify-start tw-text-[#906F00] tw-text-xs tw-font-medium tw-leading-4">AB Testing - In Progress</div>
                    </div>
                )}
            </div>

            {/* Model Section */}
            <div className="tw-mb-4">
                {applet.ab_testing_enabled ? (
                    <>
                        <label className="tw-block tw-text-xs tw-font-semibold tw-text-grey-900 tw-mb-2">
                            Model
                        </label>
                        {/* AB Testing: Show read-only model badges */}
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                            {applet.model && applet.model.length > 0 ? (
                                applet.model.map((model) => (
                                    <div key={model.id} className="tw-px-2.5 tw-py-2 tw-bg-grey-100 tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-grey-200 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5">
                                        <span
                                            id={`${applet.applet_id}-ab-model-${model.id}`}
                                            className="tw-text-sm tw-text-gray-800"
                                            data-testid={`${applet.applet_id}-ab-model-${model.id}`}
                                        >
                                            {model.name}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <span className="tw-text-gray-500 tw-text-sm">No models in AB test</span>
                            )}
                        </div>
                    </>
                ) : (
                    /* Normal State: Show dropdown for model selection */
                    <Dropdown
                        label="Model"
                        options={modelOptions}
                        value={applet.model?.[0]?.id || ""}
                        onChange={handleModelSelect}
                        placeholder="Select Model"
                        disabled={false}
                        className="tw-w-full tw-rounded-md"
                    />
                )}
            </div>

            {/* Key Section */}
            <div className="tw-mb-4">
                <div className="tw-flex tw-items-center tw-mb-2">
                    <label className="tw-text-xs tw-font-semibold tw-text-grey-900 tw-mr-1">
                        Key
                    </label>
                    <OverlayIcon
                        placement="right"
                        message={
                            <p>
                                Copy this key and add it to your ecommerce backend to display this Applet in the desired location on your website.
                            </p>
                        }
                    />
                </div>

                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-flex tw-items-center tw-flex-1 tw-px-2.5 tw-justify-between tw-py-2 tw-bg-gray-100 tw-border tw-border-grey-200 tw-rounded-md tw-text-blue-950 tw-text-sm tw-font-medium">
                        {applet.applet}
                        <button aria-label="Copy"
                            className="hover:tw-bg-gray-100 tw-rounded tw-transition-colors"
                            onClick={handleClipboard}
                            data-testid={`${applet.applet_id}-copy-key`}
                        >
                            <CopyIconBlue />
                        </button>
                    </div>
                </div>
            </div>

            {/* Placement Note Section */}
            <div>
                <label className="tw-block tw-text-xs tw-font-semibold tw-text-grey-900 tw-mb-2">
                    Placement note
                </label>
                <textarea
                    ref={noteInputRef}
                    className="tw-w-full tw-h-16 tw-px-2.5 tw-py-2 tw-bg-white tw-border tw-border-grey-200 tw-rounded-md tw-text-gray-500 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-accent-hover focus:tw-border-transparent tw-resize-none"
                    placeholder="Enter note"
                    value={note}
                    onChange={handlePlacementNoteChange}
                    data-testid={`${applet.applet_id}-placement-note`}
                />
            </div>
        </div>
    );
};

export default AppletCard;